import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Menu, PanelLeft, PanelTop, X } from "lucide-react";
import { type ReactNode, useCallback, useRef, useState } from "react";
import type { DevelopmentTool, DevelopmentToolsVisibility } from "@/development/preferences";
import { ColumnAccountToggle } from "@/features/auth/components/AccountToggles";
import { SettingsColumn } from "@/features/settings/SettingsScreen";
import { chooseArrangement, useArrangement } from "./arrangement";
import { LeaveYouContext } from "./leave-you";
import { ShellHead } from "./ShellHead";
import { useHoverIntent } from "./use-hover-intent";
import { usePhoneWidth } from "./use-phone-width";
import { Ways } from "./Ways";

/**
 * Which face the column shows, set by the page: the ways everywhere, and in settings, your face:
 * Profile and the other settings sections. Between them is one slide.
 */
type ColumnFace = "ways" | "you";

/**
 * The app's frame, in three arrangements of one idea.
 *
 * On a desktop the ways are either down the side or behind a mark in a bar
 * across the top, and the mark changes which. Changing costs the screen
 * nothing: a desktop window is mostly margin, and the margin is what pays for
 * the chrome. On a phone there is no margin to spend, so the ways become a
 * list that opens downward.
 *
 * Only two things are state here — which arrangement, and whether the pointer
 * is holding the column open. The column's face follows the page. Every
 * timing and every distance is a CSS rule on the state the frame is in, in
 * `shell.css`.
 */
export function AppShell({
  children,
  developmentTools,
  onDevelopmentToolVisibilityChange,
}: {
  children: ReactNode;
  developmentTools: DevelopmentToolsVisibility;
  onDevelopmentToolVisibilityChange: (tool: DevelopmentTool, visible: boolean) => void;
}) {
  const phone = usePhoneWidth();
  const arrangement = useArrangement();
  const [peeked, setPeeked] = useState(false);
  const [waysOpen, setWaysOpen] = useState(false);

  const pathname = useRouterState({ select: state => state.location.pathname });
  const settings = pathname === "/settings" || pathname.startsWith("/settings/");

  const face: ColumnFace = settings ? "you" : "ways";

  // The page your face of the column was opened from, so its back arrow returns there however many
  // of its tabs were visited.
  const navigate = useNavigate();
  const [youFrom, setYouFrom] = useState("/");
  const [lastPathname, setLastPathname] = useState(pathname);
  const [lastFace, setLastFace] = useState(face);
  if (pathname !== lastPathname) {
    if (face === "you" && lastFace === "ways") setYouFrom(lastPathname);
    setLastPathname(pathname);
    setLastFace(face);
  }

  const leaveYou = useCallback(() => void navigate({ to: youFrom }), [navigate, youFrom]);

  /** The two places a pointer can be and still be holding the column open. */
  const markElement = useRef<HTMLButtonElement>(null);
  const columnElement = useRef<HTMLDivElement>(null);

  const { cancel, handlers } = useHoverIntent(
    () => setPeeked(true),
    () => setPeeked(false),
    () =>
      Boolean(markElement.current?.matches(":hover") || columnElement.current?.matches(":hover")),
  );

  const inSidebar = !phone && arrangement === "sidebar";
  /** The column is standing in the frame, whether or not you have kept it. */
  const columnShowing = inSidebar || (!phone && peeked);

  /** The mark is the way between the two arrangements, both ways round. */
  function changeOver() {
    // Going to the bar has to let go of whatever the pointer was doing.
    // Going to the sidebar keeps it, and the column already standing is what
    // tells the bar it has nothing to wait for.
    if (arrangement === "sidebar") {
      cancel();
      setPeeked(false);
    }
    chooseArrangement(arrangement === "sidebar" ? "bar" : "sidebar");
  }

  return (
    <div className="h-dvh overflow-hidden bg-background">
      <div
        className="app-shell"
        data-arrangement={arrangement}
        data-column={columnShowing || undefined}
        data-peeked={peeked || undefined}
        data-face={face}
        data-ways={(phone && waysOpen) || undefined}
      >
        <ShellHead
          mark={
            phone
              ? {
                  // Down here the ways are not an arrangement to be in, they
                  // are a list that opens and shuts, and nothing says that
                  // plainer.
                  icon: waysOpen ? X : Menu,
                  label: waysOpen ? "Close navigation" : "Navigation",
                  expanded: waysOpen,
                  onPress: () => setWaysOpen(open => !open),
                }
              : {
                  // Each arrangement shows the one you would get, so the mark
                  // never sits there looking pressed for something already
                  // open.
                  icon: inSidebar ? PanelTop : PanelLeft,
                  label: inSidebar ? "Switch to the top bar" : "Switch to the sidebar",
                  onPress: changeOver,
                }
          }
          peek={phone ? undefined : handlers}
          markRef={markElement}
          pictureInColumn={inSidebar}
          onBack={inSidebar && face === "you" ? leaveYou : undefined}
          developmentTools={developmentTools}
          onDevelopmentToolVisibilityChange={onDevelopmentToolVisibilityChange}
        />

        {/* The ways are ground here too. The screen has been lying on them. */}
        <div className="shell-ways-drawer" inert={!phone || !waysOpen}>
          <div>
            <Ways onNavigate={() => setWaysOpen(false)} />
          </div>
        </div>

        {/* It stands in the room the margins gave up, hovered or kept, which
            is what makes pressing the mark not the column arriving. */}
        <div ref={columnElement} className="shell-column" inert={!columnShowing} {...handlers}>
          {/* Two faces side by side, slid to the one the page asks for. Your name and balance are at
              the foot of the ways; your face ends with Sign out instead. */}
          <div className="min-h-0 flex-1 overflow-hidden">
            <div className="shell-panes">
              <div
                inert={face !== "ways"}
                className="flex h-full w-(--column-width) shrink-0 flex-col"
              >
                <Ways />
                <div inert={!inSidebar} className="shell-column-foot flex shrink-0 p-2">
                  <ColumnAccountToggle />
                </div>
              </div>
              <div inert={face !== "you"} className="flex h-full w-(--column-width) shrink-0">
                <SettingsColumn />
              </div>
            </div>
          </div>
        </div>

        {/*
          The screen is the window minus whatever is open beside it. Its edges
          move and its content does not, so nothing re-columns.

          Sideways first, then the bar. In the sidebar the screen takes the
          full height and the bar is underneath it rather than faded out;
          letting go spreads the screen back across the window before dropping
          it to uncover the bar again.
        */}
        <div className="app-screen-frame">
          <div data-scroll-restoration-id="screen" className="app-screen">
            <LeaveYouContext value={leaveYou}>{children}</LeaveYouContext>
          </div>
        </div>
      </div>
    </div>
  );
}
